import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Action, Actions, NgxsOnInit, ofActionDispatched, Selector, State, StateContext, Store } from '@ngxs/store';
import { ActionStatus } from '@ngxs/store/src/actions-stream';
import _ from 'lodash';
import moment from 'moment';
import { ToastrService } from 'ngx-toastr';
import { StateResetAll } from 'ngxs-reset-plugin';
import { combineLatest, iif, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, pairwise, startWith, switchMap, take, tap } from 'rxjs/operators';
import { AssignedMailToastComponent } from 'src/app/components/notifications/assigned-mail-toast/assigned-mail-toast.component';
import { ChatToastComponent } from 'src/app/components/notifications/chat-toast/chat-toast.component';
import { OutboundConfirmationToastComponent } from 'src/app/components/notifications/outbound-confirmation-toast/outbound-confirmation-toast.component';
import { RingingToastComponent } from 'src/app/components/notifications/ringing-toast/ringing-toast.component';
import { UnreachableToastComponent } from 'src/app/components/notifications/unreachable-toast/unreachable-toast.component';
import { WaitingItemComponent } from 'src/app/components/notifications/waiting-item/waiting-item.component';
import { IActionErrorDescription } from 'src/app/samwebDescription/samweb/notification/IActionErrorDescription';
import { NotificationType } from 'src/app/samwebDescription/samweb/notification/notificationType';
import { RingerService } from 'src/app/services/audio/ringer.service';
import { NativeNotificationService } from 'src/app/services/notification/native-notification.service';
import { NotificationService } from 'src/app/services/notification/notification.service';
import { ServiceWorkerStaticValues } from 'src/app/services/service-workers/serviceWorkerStaticValues';
import { NotificationNativeStateFunctions } from 'src/app/store/notification/notification-native/notification-native.functions';
import { NotificationKey, NotificationNativeState } from 'src/app/store/notification/notification-native/notification-native.state';
import { SamwinUnreachableWarningSelectors } from 'src/app/store/notification/samwinUnreachableWarning/samwin-unreachable-warning.selectors';
import { DialpadNumberChangedAction, StartNewCallAction } from 'src/app/store/samwin/client-phone/client-phone.actions';
import { ClientPhoneState } from 'src/app/store/samwin/client-phone/client-phone.state';
import { SamwinLinesStateSelector } from 'src/app/store/samwin/samwin-lines/samwin-lines.selector';
import { SamwinMessageLinesStateSelectors } from 'src/app/store/samwin/samwin-message-lines/samwin-message-lines.selectors';
import { SamwinSkillsState } from 'src/app/store/samwin/samwin-skills/samwin-skills.state';
import { RealTimeConnectionStatus } from 'src/app/store/server-communication/server-communication-status';
import { ServerCommunicationState } from 'src/app/store/server-communication/server-communication.state';
import { SessionState } from 'src/app/store/session/session.state';
import { WaitingItemNotificationType } from 'src/generated/signalR/waiting-item-notification-type';
import { ChatsSelectors } from '../../samwin/chats/chats.selectors';
import {
   AcceptNewOutboundCallRequest,
   AgentIsBeingLoggedOutNotification,
   CloseNativeWithToastId,
   CloseOpenAssignedMailNotification,
   CloseOpenChatNotifications,
   CloseOpenConfirmOutboundCallNotifications,
   CloseOpenRequireMoreAgentsNotification,
   CloseOpenRingingPhoneNotifications,
   CloseOpenWaitingItemNotification,
   CloseUnreachableNotification,
   CreateNativeOutboundCallWithToastId,
   CreateNativeWithToastId,
   MarkAsClosedByTag,
   MarkAwaitingCallAsClosed,
   MarkAwaitingChatAsClosed,
   MarkAwaitingMessageAsClosed,
   MarkOutboundCallAsClosed,
   MarkRequireMoreAgentsAsClosed,
   MarkUnreachableAsClosed,
   MarkWaitingItemAsClosed,
   RemoveSingularToast,
   RequestNewOutboundCall,
   RequireMoreAgentsNotificationAdded,
   RequireMoreAgentsNotificationRemoved,
   ToastNewApplicationVersionIsAvailable,
   ToastNewAssignedMailNotification,
   ToastNewChatNotification,
   ToastNewConfirmOutboundCallNotification,
   ToastNewRingingPhoneNotification,
   ToastNewWaitingItemNotification,
   ToastRequireMoreAgentsNotification,
   ToastServerConnectionChanged,
   ToastUnreachableNotification as ToastUnreachableNotificationIfRequired,
   ToastUserInfoNotification,
   UnreachableNotificationAdded,
   UnreachableNotificationRemoved,
   UpdateOutboundCallRequestContent,
} from './notification-toastr.actions';

export interface NotificationToastrStateModel {
   openSignalRToasts: number[];
   openRingingPhoneToasts: number[];
   openWaitingItemToasts: number[];
   openChatsToasts: number[];
   openAssignedMessagesToasts: number[];
   openRequireMoreAgentsToasts: number[];
   openUnreachableToasts: number[];
   openOutboundCallToasts: number[];

   closingRingingPhoneToasts: number[];
   closingWaitingItemToasts: number[];
   closingChatsToasts: number[];
   closingAssignedMessagesToasts: number[];
   closingRequireMoreAgentsToasts: number[];
   closingUnreachableToasts: number[];
   closingOutboundCallToasts: number[];

   outboundCallRequestContent: OutboundCallRequestContent[];
}

export interface OutboundCallRequestContent {
   notificationId: number;
   numberToDial: string;
}

@State<NotificationToastrStateModel>({
   name: 'notificationToastr',
   defaults: {
      openRingingPhoneToasts: [],
      openSignalRToasts: [],
      openWaitingItemToasts: [],
      openRequireMoreAgentsToasts: [],
      openUnreachableToasts: [],
      openAssignedMessagesToasts: [],
      openChatsToasts: [],
      openOutboundCallToasts: [],

      closingRingingPhoneToasts: [],
      closingWaitingItemToasts: [],
      closingChatsToasts: [],
      closingAssignedMessagesToasts: [],
      closingRequireMoreAgentsToasts: [],
      closingUnreachableToasts: [],
      closingOutboundCallToasts: [],

      outboundCallRequestContent: [],
   },
})
@Injectable()
export class NotificationToastrState implements NgxsOnInit {
   constructor(
      private store: Store,
      private toastr: ToastrService,
      private actions$: Actions,
      private translationService: TranslateService,
      private nativeNotificationService: NativeNotificationService,
      private notificationService: NotificationService,
      private ringerService: RingerService
   ) {}

   @Selector([NotificationToastrState])
   public static openRingingPhoneToasts(state: NotificationToastrStateModel): number[] {
      return state?.openRingingPhoneToasts ?? [];
   }

   @Selector([NotificationToastrState])
   public static openWaitingItemToasts(state: NotificationToastrStateModel): number[] {
      return state?.openWaitingItemToasts ?? [];
   }

   @Selector([NotificationToastrState])
   public static openOutboundCallToasts(state: NotificationToastrStateModel): number[] {
      return state?.openOutboundCallToasts ?? [];
   }

   @Selector([NotificationToastrState])
   public static openAssignedMessagesToasts(state: NotificationToastrStateModel): number[] {
      return state?.openAssignedMessagesToasts ?? [];
   }

   @Selector([NotificationToastrState])
   public static openChatsToasts(state: NotificationToastrStateModel): number[] {
      return state?.openChatsToasts ?? [];
   }

   @Selector([NotificationToastrState])
   public static openRequireMoreAgentsToasts(state: NotificationToastrStateModel): number[] {
      return state?.openRequireMoreAgentsToasts ?? [];
   }

   @Selector([NotificationToastrState])
   public static openUnreachableToasts(state: NotificationToastrStateModel): number[] {
      return state?.openUnreachableToasts ?? [];
   }

   @Selector([NotificationToastrState])
   public static closingRingingPhoneToasts(state: NotificationToastrStateModel): number[] {
      return state?.closingRingingPhoneToasts ?? [];
   }

   @Selector([NotificationToastrState])
   public static closingWaitingItemToasts(state: NotificationToastrStateModel): number[] {
      return state?.closingWaitingItemToasts ?? [];
   }

   @Selector([NotificationToastrState])
   public static closingOutboundCallToasts(state: NotificationToastrStateModel): number[] {
      return state?.closingOutboundCallToasts ?? [];
   }

   @Selector([NotificationToastrState])
   public static closingAssignedMessagesToasts(state: NotificationToastrStateModel): number[] {
      return state?.closingAssignedMessagesToasts ?? [];
   }

   @Selector([NotificationToastrState])
   public static closingChatsToasts(state: NotificationToastrStateModel): number[] {
      return state?.closingChatsToasts ?? [];
   }

   @Selector([NotificationToastrState])
   public static closingRequireMoreAgentsToasts(state: NotificationToastrStateModel): number[] {
      return state?.closingRequireMoreAgentsToasts ?? [];
   }

   @Selector([NotificationToastrState])
   public static closingUnreachableToasts(state: NotificationToastrStateModel): number[] {
      return state?.closingUnreachableToasts ?? [];
   }

   @Selector()
   public static isToastAvailable(): (toasts: number[]) => boolean {
      return (toasts: number[]) => {
         return !_.isEmpty(toasts);
      };
   }

   @Selector()
   public static getMostRecent() {
      return (toasts: number[]) => {
         if (!_.isUndefined(toasts) && !_.isEmpty(toasts)) {
            return _(toasts).max();
         }
         return null;
      };
   }

   @Selector([NotificationToastrState.openRingingPhoneToasts, NotificationToastrState.isToastAvailable])
   public static isRingingPhoneToastOpen(toasts: number[], isOpen: (toasts: number[]) => boolean): boolean {
      return isOpen(toasts);
   }

   @Selector([NotificationToastrState.openWaitingItemToasts, NotificationToastrState.isToastAvailable])
   public static isWaitingItemToastOpen(toasts: number[], isOpen: (toasts: number[]) => boolean): boolean {
      return isOpen(toasts);
   }

   @Selector([NotificationToastrState.openAssignedMessagesToasts, NotificationToastrState.isToastAvailable])
   public static isAssignedMessagesToastOpen(toasts: number[], isOpen: (toasts: number[]) => boolean): boolean {
      return isOpen(toasts);
   }

   @Selector([NotificationToastrState.openChatsToasts, NotificationToastrState.isToastAvailable])
   public static isChatsToastOpen(toasts: number[], isOpen: (toasts: number[]) => boolean): boolean {
      return isOpen(toasts);
   }

   @Selector([NotificationToastrState.openRequireMoreAgentsToasts, NotificationToastrState.isToastAvailable])
   public static isRequireMoreAgentsToastOpen(toasts: number[], isOpen: (toasts: number[]) => boolean): boolean {
      return isOpen(toasts);
   }

   @Selector([NotificationToastrState.openUnreachableToasts, NotificationToastrState.isToastAvailable])
   public static isUnreachableToastOpen(toasts: number[], isOpen: (toasts: number[]) => boolean): boolean {
      return isOpen(toasts);
   }

   @Selector([NotificationToastrState.getMostRecent, NotificationToastrState.openRingingPhoneToasts])
   public static mostRecentRingingPhoneToast(getMostRecent: (toasts: number[]) => number, toasts: number[]) {
      return getMostRecent(toasts);
   }

   @Selector([NotificationToastrState.getMostRecent, NotificationToastrState.openWaitingItemToasts])
   public static mostRecentWaitingItemToast(getMostRecent: (toasts: number[]) => number, toasts: number[]) {
      return getMostRecent(toasts);
   }

   @Selector([NotificationToastrState.getMostRecent, NotificationToastrState.openAssignedMessagesToasts])
   public static mostRecentAssignedMessagesToast(getMostRecent: (toasts: number[]) => number, toasts: number[]) {
      return getMostRecent(toasts);
   }

   @Selector([NotificationToastrState.getMostRecent, NotificationToastrState.openChatsToasts])
   public static mostRecentChatsToast(getMostRecent: (toasts: number[]) => number, toasts: number[]) {
      return getMostRecent(toasts);
   }

   @Selector([NotificationToastrState.getMostRecent, NotificationToastrState.openRequireMoreAgentsToasts])
   public static mostRecentRequireMoreAgentsToast(getMostRecent: (toasts: number[]) => number, toasts: number[]) {
      return getMostRecent(toasts);
   }

   @Selector([NotificationToastrState.getMostRecent, NotificationToastrState.openUnreachableToasts])
   public static mostRecentUnreachableToast(getMostRecent: (toasts: number[]) => number, toasts: number[]) {
      return getMostRecent(toasts);
   }

   @Selector([NotificationToastrState.getMostRecent, NotificationToastrState.closingRingingPhoneToasts])
   public static mostRecentClosedRingingPhoneToast(getMostRecent: (toasts: number[]) => number, toasts: number[]) {
      return getMostRecent(toasts);
   }

   @Selector([NotificationToastrState.getMostRecent, NotificationToastrState.closingWaitingItemToasts])
   public static mostRecentClosedwaitingItemToast(getMostRecent: (toasts: number[]) => number, toasts: number[]) {
      return getMostRecent(toasts);
   }

   @Selector([NotificationToastrState.getMostRecent, NotificationToastrState.closingOutboundCallToasts])
   public static mostRecentClosedOutboundCallToast(getMostRecent: (toasts: number[]) => number, toasts: number[]) {
      return getMostRecent(toasts);
   }

   @Selector([NotificationToastrState.getMostRecent, NotificationToastrState.closingAssignedMessagesToasts])
   public static mostRecentClosedAssignedMessagesToast(getMostRecent: (toasts: number[]) => number, toasts: number[]) {
      return getMostRecent(toasts);
   }

   @Selector([NotificationToastrState.getMostRecent, NotificationToastrState.closingChatsToasts])
   public static mostRecentClosedChatsToast(getMostRecent: (toasts: number[]) => number, toasts: number[]) {
      return getMostRecent(toasts);
   }

   @Selector([NotificationToastrState.getMostRecent, NotificationToastrState.closingRequireMoreAgentsToasts])
   public static mostRecentClosedRequireMoreAgentsToast(getMostRecent: (toasts: number[]) => number, toasts: number[]) {
      return getMostRecent(toasts);
   }

   @Selector([NotificationToastrState.getMostRecent, NotificationToastrState.closingUnreachableToasts])
   public static mostRecentClosedUnreachableToast(getMostRecent: (toasts: number[]) => number, toasts: number[]) {
      return getMostRecent(toasts);
   }

   @Selector()
   public static getToastsWithNativeTag() {
      return (tag: string, toasts: number[]) => {
         return toasts.map((id) => {
            return { tag: tag, id: id };
         });
      };
   }

   @Selector([NotificationToastrState.openRingingPhoneToasts, NotificationToastrState.getToastsWithNativeTag])
   private static openRingingPhoneToastsWithTags(
      ringingPhoneToasts: number[],
      getToastsWithNativeTag: (
         tag: string,
         toasts: number[]
      ) => {
         tag: string;
         id: number;
      }[]
   ) {
      return getToastsWithNativeTag(ServiceWorkerStaticValues.AWAITING_CALL_NOTIFICATION_TAG, ringingPhoneToasts);
   }

   @Selector([NotificationToastrState.openWaitingItemToasts, NotificationToastrState.getToastsWithNativeTag])
   private static openWaitingItemToastsWithTags(
      waitingItemToasts: number[],
      getToastsWihNativeTag: (
         tag: string,
         toasts: number[]
      ) => {
         tag: string;
         id: number;
      }[]
   ) {
      return getToastsWihNativeTag(ServiceWorkerStaticValues.WAITING_ITEM_NOTIFICATION_TAG, waitingItemToasts);
   }

   @Selector([NotificationToastrState.openOutboundCallToasts, NotificationToastrState.getToastsWithNativeTag])
   private static openOutboundCallToastsWithTags(
      outboundCallToasts: number[],
      getToastsWithNativeTag: (
         tag: string,
         toasts: number[]
      ) => {
         tag: string;
         id: number;
      }[]
   ) {
      return getToastsWithNativeTag(ServiceWorkerStaticValues.NEW_OUTBOUND_CALL_REQUEST_TAG, outboundCallToasts);
   }

   @Selector([NotificationToastrState.openAssignedMessagesToasts, NotificationToastrState.getToastsWithNativeTag])
   private static openAssignedMessagesToastsWithTags(
      openAssignedMessagesToasts: number[],
      getToastsWithNativeTag: (
         tag: string,
         toasts: number[]
      ) => {
         tag: string;
         id: number;
      }[]
   ) {
      return getToastsWithNativeTag(ServiceWorkerStaticValues.ASSIGNED_MESSAGE_NOTIFICATION_TAG, openAssignedMessagesToasts);
   }

   @Selector([NotificationToastrState.openChatsToasts, NotificationToastrState.getToastsWithNativeTag])
   private static openChatsToastsWithTags(
      openChatsToasts: number[],
      getToastsWithNativeTag: (
         tag: string,
         toasts: number[]
      ) => {
         tag: string;
         id: number;
      }[]
   ) {
      return getToastsWithNativeTag(ServiceWorkerStaticValues.AWAITING_CHAT_NOTIFICATION_TAG, openChatsToasts);
   }

   @Selector([NotificationToastrState.openRequireMoreAgentsToasts, NotificationToastrState.getToastsWithNativeTag])
   private static openRequireMoreAgentsToastsWithTags(
      openRequireMoreAgentsToasts: number[],
      getToastsWithNativeTag: (
         tag: string,
         toasts: number[]
      ) => {
         tag: string;
         id: number;
      }[]
   ) {
      return getToastsWithNativeTag(ServiceWorkerStaticValues.REQUIRE_AGENTS_NOTIFICATION_TAG, openRequireMoreAgentsToasts);
   }

   @Selector([NotificationToastrState.openUnreachableToasts, NotificationToastrState.getToastsWithNativeTag])
   private static openUnreachableToastsWithTags(
      openRequireMoreAgentsToasts: number[],
      getToastsWithNativeTag: (
         tag: string,
         toasts: number[]
      ) => {
         tag: string;
         id: number;
      }[]
   ) {
      return getToastsWithNativeTag(ServiceWorkerStaticValues.UNREACHABLE_NOTIFICATION_TAG, openRequireMoreAgentsToasts);
   }

   @Selector([NotificationToastrState.closingRingingPhoneToasts, NotificationToastrState.getToastsWithNativeTag])
   private static closingRingingPhoneToastsWithTags(
      ringingPhoneToasts: number[],
      getToastsWithNativeTag: (
         tag: string,
         toasts: number[]
      ) => {
         tag: string;
         id: number;
      }[]
   ) {
      return getToastsWithNativeTag(ServiceWorkerStaticValues.AWAITING_CALL_NOTIFICATION_TAG, ringingPhoneToasts);
   }

   @Selector([NotificationToastrState.closingWaitingItemToasts, NotificationToastrState.getToastsWithNativeTag])
   private static closingWaitingItemToastsWithTags(
      waitingItemToasts: number[],
      getToastsWihNativeTag: (
         tag: string,
         toasts: number[]
      ) => {
         tag: string;
         id: number;
      }[]
   ) {
      return getToastsWihNativeTag(ServiceWorkerStaticValues.WAITING_ITEM_NOTIFICATION_TAG, waitingItemToasts);
   }

   @Selector([NotificationToastrState.closingOutboundCallToasts, NotificationToastrState.getToastsWithNativeTag])
   private static closingOutboundCallToastsWithTags(
      outboundCallToasts: number[],
      getToastsWithNativeTag: (
         tag: string,
         toasts: number[]
      ) => {
         tag: string;
         id: number;
      }[]
   ) {
      return getToastsWithNativeTag(ServiceWorkerStaticValues.NEW_OUTBOUND_CALL_REQUEST_TAG, outboundCallToasts);
   }

   @Selector([NotificationToastrState.closingAssignedMessagesToasts, NotificationToastrState.getToastsWithNativeTag])
   private static closingAssignedMessagesToastsWithTags(
      openAssignedMessagesToasts: number[],
      getToastsWithNativeTag: (
         tag: string,
         toasts: number[]
      ) => {
         tag: string;
         id: number;
      }[]
   ) {
      return getToastsWithNativeTag(ServiceWorkerStaticValues.ASSIGNED_MESSAGE_NOTIFICATION_TAG, openAssignedMessagesToasts);
   }

   @Selector([NotificationToastrState.closingChatsToasts, NotificationToastrState.getToastsWithNativeTag])
   private static closingChatsToastsWithTags(
      openChatsToasts: number[],
      getToastsWithNativeTag: (
         tag: string,
         toasts: number[]
      ) => {
         tag: string;
         id: number;
      }[]
   ) {
      return getToastsWithNativeTag(ServiceWorkerStaticValues.AWAITING_CHAT_NOTIFICATION_TAG, openChatsToasts);
   }

   @Selector([NotificationToastrState.closingRequireMoreAgentsToasts, NotificationToastrState.getToastsWithNativeTag])
   private static closingRequireMoreAgentsToastsWithTags(
      openRequireMoreAgentsToasts: number[],
      getToastsWithNativeTag: (
         tag: string,
         toasts: number[]
      ) => {
         tag: string;
         id: number;
      }[]
   ) {
      return getToastsWithNativeTag(ServiceWorkerStaticValues.REQUIRE_AGENTS_NOTIFICATION_TAG, openRequireMoreAgentsToasts);
   }

   @Selector([NotificationToastrState.closingUnreachableToasts, NotificationToastrState.getToastsWithNativeTag])
   private static closingUnreachableToastsWithTags(
      openRequireMoreAgentsToasts: number[],
      getToastsWithNativeTag: (
         tag: string,
         toasts: number[]
      ) => {
         tag: string;
         id: number;
      }[]
   ) {
      return getToastsWithNativeTag(ServiceWorkerStaticValues.UNREACHABLE_NOTIFICATION_TAG, openRequireMoreAgentsToasts);
   }

   @Selector([
      NotificationToastrState.openRingingPhoneToastsWithTags,
      NotificationToastrState.openAssignedMessagesToastsWithTags,
      NotificationToastrState.openChatsToastsWithTags,
      NotificationToastrState.openRequireMoreAgentsToastsWithTags,
      NotificationToastrState.openUnreachableToastsWithTags,
      NotificationToastrState.openWaitingItemToastsWithTags,
      NotificationNativeState.getHighestFiredId,
   ])
   public static openToastsWithTags(
      ringingPhoneKeys: NotificationKey[],
      assignedMessagesKeys: NotificationKey[],
      chatsKeys: NotificationKey[],
      requireMoreAgentsKeys: NotificationKey[],
      unreachableKeys: NotificationKey[],
      waitingItemKeys: NotificationKey[],
      getHighestFiredId: (tag: string) => number
   ) {
      return _.concat(
         ringingPhoneKeys.filter((key) => key.id > getHighestFiredId(key.tag)),
         assignedMessagesKeys.filter((key) => key.id > getHighestFiredId(key.tag)),
         chatsKeys.filter((key) => key.id > getHighestFiredId(key.tag)),
         requireMoreAgentsKeys.filter((key) => key.id > getHighestFiredId(key.tag)),
         waitingItemKeys.filter((key) => key.id > getHighestFiredId(key.tag)),
         unreachableKeys.filter((key) => key.id > getHighestFiredId(key.tag))
      );
   }

   @Selector([
      NotificationToastrState.openRingingPhoneToastsWithTags,
      NotificationToastrState.openAssignedMessagesToastsWithTags,
      NotificationToastrState.openChatsToastsWithTags,
      NotificationToastrState.openRequireMoreAgentsToastsWithTags,
      NotificationToastrState.openUnreachableToastsWithTags,
      NotificationToastrState.openWaitingItemToastsWithTags,
      NotificationNativeState.getHighestFiredId,
   ])
   public static openToastsWithTagsToLow(
      ringingPhoneKeys: NotificationKey[],
      assignedMessagesKeys: NotificationKey[],
      chatsKeys: NotificationKey[],
      requireMoreAgentsKeys: NotificationKey[],
      unreachableKeys: NotificationKey[],
      waitingItemKeys: NotificationKey[],
      getHighestFiredId: (tag: string) => number
   ) {
      return _.concat(
         ringingPhoneKeys.filter((key) => key.id < getHighestFiredId(key.tag)),
         assignedMessagesKeys.filter((key) => key.id < getHighestFiredId(key.tag)),
         chatsKeys.filter((key) => key.id < getHighestFiredId(key.tag)),
         requireMoreAgentsKeys.filter((key) => key.id < getHighestFiredId(key.tag)),
         waitingItemKeys.filter((key) => key.id < getHighestFiredId(key.tag)),
         unreachableKeys.filter((key) => key.id < getHighestFiredId(key.tag))
      );
   }

   @Selector([NotificationToastrState.openOutboundCallToastsWithTags, NotificationNativeState.getHighestFiredId])
   public static openToastsWithCallIds(outboundCallKeys: NotificationKey[], getHighestFiredId: (tag: string) => number) {
      return outboundCallKeys.filter((key) => key.id > getHighestFiredId(key.tag));
   }

   @Selector([
      NotificationToastrState.closingRingingPhoneToastsWithTags,
      NotificationToastrState.closingOutboundCallToastsWithTags,
      NotificationToastrState.closingAssignedMessagesToastsWithTags,
      NotificationToastrState.closingChatsToastsWithTags,
      NotificationToastrState.closingRequireMoreAgentsToastsWithTags,
      NotificationToastrState.closingUnreachableToastsWithTags,
      NotificationToastrState.closingWaitingItemToastsWithTags,
      NotificationNativeState.getHighestFiredId,
   ])
   public static closingToastsWithTags(
      ringingPhoneKeys: NotificationKey[],
      outboundCallKeys: NotificationKey[],
      assignedMessagesKeys: NotificationKey[],
      chatsKeys: NotificationKey[],
      requireMoreAgentsKeys: NotificationKey[],
      unreachableKeys: NotificationKey[],
      waitingItemKeys: NotificationKey[],
      getHighestFiredId: (tag: string) => number
   ) {
      return _.concat(
         ringingPhoneKeys.filter((key) => key.id >= getHighestFiredId(key.tag)),
         outboundCallKeys.filter((key) => key.id >= getHighestFiredId(key.tag)),
         assignedMessagesKeys.filter((key) => key.id >= getHighestFiredId(key.tag)),
         chatsKeys.filter((key) => key.id >= getHighestFiredId(key.tag)),
         requireMoreAgentsKeys.filter((key) => key.id >= getHighestFiredId(key.tag)),
         waitingItemKeys.filter((key) => key.id >= getHighestFiredId(key.tag)),
         unreachableKeys.filter((key) => key.id >= getHighestFiredId(key.tag))
      );
   }

   @Selector([
      NotificationToastrState.closingRingingPhoneToastsWithTags,
      NotificationToastrState.closingOutboundCallToastsWithTags,
      NotificationToastrState.closingAssignedMessagesToastsWithTags,
      NotificationToastrState.closingChatsToastsWithTags,
      NotificationToastrState.closingRequireMoreAgentsToastsWithTags,
      NotificationToastrState.closingUnreachableToastsWithTags,
      NotificationToastrState.closingWaitingItemToastsWithTags,
      NotificationNativeState.getHighestFiredId,
   ])
   public static closingToastsWithTagsToLow(
      ringingPhoneKeys: NotificationKey[],
      outboundCallKeys: NotificationKey[],
      assignedMessagesKeys: NotificationKey[],
      chatsKeys: NotificationKey[],
      requireMoreAgentsKeys: NotificationKey[],
      unreachableKeys: NotificationKey[],
      waitingItemKeys: NotificationKey[],
      getHighestFiredId: (tag: string) => number
   ) {
      return _.concat(
         ringingPhoneKeys.filter((key) => key.id < getHighestFiredId(key.tag)),
         outboundCallKeys.filter((key) => key.id < getHighestFiredId(key.tag)),
         assignedMessagesKeys.filter((key) => key.id < getHighestFiredId(key.tag)),
         chatsKeys.filter((key) => key.id < getHighestFiredId(key.tag)),
         requireMoreAgentsKeys.filter((key) => key.id < getHighestFiredId(key.tag)),
         waitingItemKeys.filter((key) => key.id < getHighestFiredId(key.tag)),
         unreachableKeys.filter((key) => key.id < getHighestFiredId(key.tag))
      );
   }

   public ngxsOnInit() {
      combineLatest([
         this.store.select(SamwinLinesStateSelector.getRingingCallId),
         this.store.select(ClientPhoneState.isAcceptOrStartCallDecisionRequired),
         this.store.select(NotificationToastrState.mostRecentRingingPhoneToast),
      ])
         .pipe(
            distinctUntilChanged(null, ([e]) => e),
            tap(([ringingCallId, isAcceptOrStartCallDecisionRequired, toastId]) => {
               if (!_.isNil(ringingCallId) && !_.isNil(toastId)) {
                  this.store.dispatch(new RemoveSingularToast(toastId));
               }
            }),
            tap(([ringingCallId, isAcceptOrStartCallDecisionRequired]) => {
               if (!_.isNil(ringingCallId) && !isAcceptOrStartCallDecisionRequired) {
                  return this.store.dispatch(new ToastNewRingingPhoneNotification());
               }
               if (_.isNil(ringingCallId)) {
                  return this.store.dispatch(new CloseOpenRingingPhoneNotifications());
               }
            })
         )
         .subscribe();

      combineLatest([
         this.store.select(SamwinMessageLinesStateSelectors.getCurrentAssignedMessage),
         this.store.select(NotificationToastrState.mostRecentAssignedMessagesToast),
      ])
         .pipe(
            distinctUntilChanged(null, ([e]) => e?.messageId),
            tap(([message, toastId]) => {
               if (!_.isNil(message?.messageId) && !_.isNil(toastId)) {
                  this.store.dispatch(new RemoveSingularToast(toastId));
               }
            }),
            map(([message]) => message),
            tap((message) => {
               if (!_.isNil(message?.messageId)) {
                  this.store.dispatch(new ToastNewAssignedMailNotification());
               }
               if (_.isNil(message?.messageId)) {
                  this.store.dispatch(new CloseOpenAssignedMailNotification());
               }
            })
         )
         .subscribe();

      combineLatest([this.store.select(ChatsSelectors.getRingingChatId), this.store.select(NotificationToastrState.mostRecentChatsToast)])
         .pipe(
            distinctUntilChanged(null, ([e]) => e?.messageId),
            tap(([chatId, toastId]) => {
               if (!_.isNil(chatId?.messageId) && !_.isNil(toastId)) {
                  this.store.dispatch(new RemoveSingularToast(toastId));
               }
            }),
            map(([chatId]) => chatId),
            distinctUntilChanged((prev, curr) => _.isEqual(prev, curr) || (!_.isNil(prev) && !_.isNil(curr))),
            tap((chatId) => {
               if (!_.isNil(chatId?.messageId)) {
                  this.store.dispatch(new ToastNewChatNotification());
               }
               if (_.isNil(chatId?.messageId)) {
                  this.store.dispatch(new CloseOpenChatNotifications());
               }
            })
         )
         .subscribe();

      combineLatest([
         this.store.select(ServerCommunicationState.getRealtimeConnectionState),
         this.store.select(ServerCommunicationState.getIsRegistrationConfirmedAtServer),
      ])
         .pipe(
            filter(([connectionState, isConfirmed]) => connectionState !== RealTimeConnectionStatus.CONNECTING),
            distinctUntilChanged((prev, curr) => _.isEqual(prev, curr)),
            tap(([connectionState, isConfirmed]) => {
               if (connectionState === RealTimeConnectionStatus.CONNECTED && isConfirmed) {
                  this.store.dispatch(new ToastServerConnectionChanged(connectionState, isConfirmed));
               }
            }),
            debounceTime(3000),
            tap(([connectionState, isConfirmed]) => this.store.dispatch(new ToastServerConnectionChanged(connectionState, isConfirmed)))
         )
         .subscribe();

      this.store
         .select(ServerCommunicationState.getRealtimeConnectionState)
         .pipe(
            switchMap((realTimeConnectionStatus: RealTimeConnectionStatus) => {
               if (realTimeConnectionStatus !== RealTimeConnectionStatus.CONNECTED) {
                  // as long as no SignalR connection is established, we hide generic error toasts
                  return of(null);
               }
               return this.actions$.pipe(
                  filter((x) => x.status === ActionStatus.Errored),
                  tap((action) => console.warn('Action finished with status error', action)),
                  tap((action) => {
                     const actionErrorDescription: IActionErrorDescription = action.action.actionErrorDescriptionFromDecorator;
                     if (_.isNil(actionErrorDescription) || actionErrorDescription.ignoreActionFailure) {
                        return;
                     }
                     const notificationType = _.isNil(actionErrorDescription.notificationType)
                        ? NotificationType.WARN
                        : actionErrorDescription.notificationType;
                     const titleTranslationKey = _.isNil(actionErrorDescription.titleTranslationKey)
                        ? 'notification.default.generic_error.title'
                        : actionErrorDescription.titleTranslationKey;
                     const messageTranslationKey = _.isNil(actionErrorDescription.messageTranslationKey)
                        ? 'notification.default.generic_error.message'
                        : actionErrorDescription.messageTranslationKey;
                     this.store.dispatch(new ToastUserInfoNotification(notificationType, titleTranslationKey, messageTranslationKey));
                  })
               );
            })
         )
         .subscribe();

      this.SubscribeToUnreachableNotification();
      combineLatest([this.store.select(SamwinSkillsState.areVisibleSkillsLoading), this.store.select(SamwinSkillsState.getUnderstaffedSkills(false))])
         .pipe(
            filter(([areVisibleSkillsLoading, understaffedSkills]) => !areVisibleSkillsLoading),
            map(([areVisibleSkillsLoading, understaffedSkills]) => understaffedSkills),
            debounceTime(500),
            distinctUntilChanged((prev, curr) => _.isEqual(prev, curr)),
            tap((underStaffedSkills) => {
               if (!_.isNil(underStaffedSkills) && !_.isEmpty(underStaffedSkills)) {
                  this.store.dispatch(new ToastRequireMoreAgentsNotification(underStaffedSkills));
               } else {
                  this.store.dispatch(new CloseOpenRequireMoreAgentsNotification());
               }
            })
         )
         .subscribe();

      this.actions$
         .pipe(
            ofActionDispatched(StateResetAll),
            tap(() => this.toastr.toasts.forEach((toast) => this.toastr.remove(toast.toastId)))
         )
         .subscribe();

      this.store
         .select(NotificationToastrState.openToastsWithTagsToLow)
         .pipe(debounceTime(200))
         .pipe(distinctUntilChanged())
         .pipe(
            tap((toasts) =>
               toasts.forEach((toast) => {
                  this.store.dispatch(new RemoveSingularToast(toast.id));
               })
            )
         )
         .subscribe();

      this.store
         .select(NotificationToastrState.openToastsWithTags)
         .pipe(debounceTime(200))
         .pipe(distinctUntilChanged())
         .pipe(
            tap((toasts) =>
               toasts.forEach((toast) => {
                  this.store.dispatch(new CreateNativeWithToastId(toast.tag, toast.id));
               })
            )
         )
         .subscribe();

      this.store
         .select(NotificationToastrState.openToastsWithCallIds)
         .pipe(debounceTime(200))
         .pipe(distinctUntilChanged())
         .pipe(
            tap((toasts) =>
               toasts.forEach((toast) => {
                  this.store.dispatch(new CreateNativeOutboundCallWithToastId(toast.tag, toast.id));
               })
            )
         )
         .subscribe();

      this.store
         .select(NotificationToastrState.closingToastsWithTagsToLow)
         .pipe(distinctUntilChanged())
         .pipe(
            tap((toasts) =>
               toasts.forEach((toast) => {
                  this.store.dispatch(new RemoveSingularToast(toast.id));
                  this.store.dispatch(new MarkAsClosedByTag(toast.tag, toast.id));
               })
            )
         )
         .subscribe();

      this.store
         .select(NotificationToastrState.closingToastsWithTags)
         .pipe(distinctUntilChanged())
         .pipe(
            tap((toasts) =>
               toasts.forEach((toast) => {
                  this.store.dispatch(new RemoveSingularToast(toast.id));
                  this.store.dispatch(new MarkAsClosedByTag(toast.tag, toast.id));
               })
            )
         )
         .pipe(
            tap((toasts) =>
               toasts.forEach((toast) => {
                  this.store.dispatch(new CloseNativeWithToastId(toast.tag, toast.id));
               })
            )
         )
         .subscribe();

      combineLatest([
         this.store.select(SessionState.getUserStateNotificationwindowRepeat),
         this.store.select(SessionState.getUserStateNotificationwindowInterval),
      ])
         .pipe(
            switchMap(([canRepeat, interval]) =>
               iif(
                  () => canRepeat,
                  combineLatest([
                     this.store.select(NotificationToastrState.isUnreachableToastOpen),
                     this.store.select(NotificationToastrState.mostRecentUnreachableToast),
                     this.store.select(NotificationNativeStateFunctions.isNotificationWithTagOpen(ServiceWorkerStaticValues.UNREACHABLE_NOTIFICATION_TAG)),
                  ]).pipe(
                     debounceTime(interval.asMilliseconds()),
                     filter(() => interval.asMilliseconds() > 0),
                     tap(([isUnreachableToastOpen, mostRecentToastId, isNativeDisplayed]) => {
                        if (isUnreachableToastOpen && !_.isNil(mostRecentToastId) && !isNativeDisplayed) {
                           this.store.dispatch(new CreateNativeWithToastId(ServiceWorkerStaticValues.UNREACHABLE_NOTIFICATION_TAG, mostRecentToastId));
                        }
                     })
                  ),
                  of(null)
               )
            )
         )
         .subscribe();

      const waitingItems$ = this.store.select(SamwinLinesStateSelector.isItemWaiting).pipe(
         filter((x) => x),
         switchMap(() =>
            combineLatest([
               this.store.select(SamwinLinesStateSelector.skillsOfWaitingItems),
               this.store.select(SamwinLinesStateSelector.waitingItemNotificationType),
            ])
         ),
         distinctUntilChanged((prev, curr) => prev[0] && curr[0] && prev[0].length === curr[0].length)
      );

      waitingItems$
         .pipe(
            filter(
               ([skills, notificationType]) =>
                  notificationType === WaitingItemNotificationType.BOTH || notificationType === WaitingItemNotificationType.VISUAL_ONLY
            ),
            tap(([skills, notificationType]) => {
               this.store.dispatch(new ToastNewWaitingItemNotification(skills));
            })
         )
         .subscribe();

      waitingItems$
         .pipe(
            filter(
               ([skills, notificationType]) =>
                  notificationType === WaitingItemNotificationType.BOTH || notificationType === WaitingItemNotificationType.ACOUSTIC_ONLY
            ),
            startWith([[], null]),
            pairwise(),
            filter(([previousValue, newValue]) => previousValue[0].length < newValue[0].length),
            tap(() => {
               this.ringerService.fireSignalTone(true);
            })
         )
         .subscribe();
   }

   @Action(ToastRequireMoreAgentsNotification)
   public toastRequireMoreAgentsNotification(ctx: StateContext<NotificationToastrStateModel>, action: ToastRequireMoreAgentsNotification) {
      const linebreak = '<br/>';
      const header: string = this.translationService.instant('notification.required_agents.title');
      let message: string = this.translationService.instant('notification.required_agents.message');
      const requiredAgentList = action.understaffedSkills;

      requiredAgentList.forEach((skill) => {
         const showSubSkills = skill.subSkills.filter((subskill) => subskill.requiredAgents < skill.requiredAgents).length > 0;
         if (showSubSkills) {
            message = `${message}${linebreak} - `;
            message = `${message}${skill.skillName} `;
            _(skill.subSkills)
               .orderBy((subskill) => subskill.skillName.toLowerCase())
               .forEach((subskill) => {
                  message = `${message}${linebreak} — `;
                  message = `${message}${subskill.skillName} `;
                  message = `${message} ${this.translationService.instant('notification.required_agents.x_of_y_required_agents_active', {
                     activeAgents: subskill.activeAgents,
                     requiredAgents: subskill.requiredAgents,
                  })}`;
               });
         } else {
            message = `${message}${linebreak} - `;
            message = `${message}${skill.skillName} `;
            message = `${message} ${this.translationService.instant('notification.required_agents.x_of_y_required_agents_active', {
               activeAgents: skill.activeAgents,
               requiredAgents: skill.requiredAgents,
            })}`;
         }
      });

      const toast = this.notificationService.showDefaultToast(NotificationType.INFO, header, message, {
         disableTimeOut: true,
         progressBar: false,
         newestOnTop: false,
         enableHtml: true,
      });

      toast.onShown.toPromise().then(() => ctx.dispatch(new RequireMoreAgentsNotificationAdded(toast.toastId)));
      toast.onHidden.toPromise().then(() => ctx.dispatch(new RequireMoreAgentsNotificationRemoved(toast.toastId)));
   }

   @Action(RequireMoreAgentsNotificationAdded)
   public RequireMoreAgentsNotificationAdded(ctx: StateContext<NotificationToastrStateModel>, action: RequireMoreAgentsNotificationAdded) {
      const openToasts = _.cloneDeep(ctx.getState()?.openRequireMoreAgentsToasts ?? []);
      const closingtoasts = _.cloneDeep(ctx.getState()?.closingRequireMoreAgentsToasts ?? []);
      ctx.patchState({
         openRequireMoreAgentsToasts: [action.toastId],
         closingRequireMoreAgentsToasts: _.difference(_.concat(openToasts, closingtoasts), [action.toastId]),
      });
   }

   @Action(RequireMoreAgentsNotificationRemoved)
   public requireMoreAgentsNotificationRemoved(ctx: StateContext<NotificationToastrStateModel>, action: RequireMoreAgentsNotificationRemoved) {
      const openToasts = _.cloneDeep(ctx.getState()?.openRequireMoreAgentsToasts ?? []);
      const closingtoasts = _.cloneDeep(ctx.getState()?.closingRequireMoreAgentsToasts ?? []);
      ctx.patchState({
         openRequireMoreAgentsToasts: _.difference(openToasts, [action.toastId]),
         closingRequireMoreAgentsToasts: _.concat(closingtoasts, [action.toastId]),
      });
   }

   @Action(CloseOpenRequireMoreAgentsNotification)
   public closeOpenRequireMoreAgentsNotification(ctx: StateContext<NotificationToastrStateModel>, action: CloseOpenRequireMoreAgentsNotification) {
      const openToasts = _.cloneDeep(ctx.getState()?.openRequireMoreAgentsToasts ?? []);
      const closingToasts = _.cloneDeep(ctx.getState()?.closingRequireMoreAgentsToasts ?? []);
      ctx.patchState({
         openRequireMoreAgentsToasts: [],
         closingRequireMoreAgentsToasts: _.concat(openToasts, closingToasts),
      });
   }

   @Action(ToastNewAssignedMailNotification)
   public toastNewAssignedMailNotification(ctx: StateContext<NotificationToastrStateModel>, action: ToastNewAssignedMailNotification) {
      const toast = this.notificationService.showDefaultToast(NotificationType.SHOW, 'New Assigned Mail', 'Mail Data', {
         positionClass: 'toast-bottom-center',
         timeOut: 0,
         extendedTimeOut: 0,
         progressBar: false,
         closeButton: false,
         tapToDismiss: false,
         toastComponent: AssignedMailToastComponent,
      });
      const openToasts = _.cloneDeep(ctx.getState()?.openAssignedMessagesToasts ?? []);
      const closingtoasts = _.cloneDeep(ctx.getState()?.closingAssignedMessagesToasts ?? []);
      ctx.patchState({
         openAssignedMessagesToasts: [toast.toastId],
         closingAssignedMessagesToasts: _.difference(_.concat(openToasts, closingtoasts), [toast.toastId]),
      });
   }

   @Action(CloseOpenAssignedMailNotification)
   public closeOpenAssignedMailNotification(ctx: StateContext<NotificationToastrStateModel>, action: CloseOpenAssignedMailNotification) {
      const openToasts = _.cloneDeep(ctx.getState()?.openAssignedMessagesToasts ?? []);
      const closingToasts = _.cloneDeep(ctx.getState()?.closingAssignedMessagesToasts ?? []);
      ctx.patchState({
         openAssignedMessagesToasts: [],
         closingAssignedMessagesToasts: _.concat(openToasts, closingToasts),
      });
   }

   @Action(ToastNewRingingPhoneNotification)
   public toastNewRingingPhoneNotification(ctx: StateContext<NotificationToastrStateModel>, action: ToastNewRingingPhoneNotification) {
      const toast = this.notificationService.showDefaultToast(NotificationType.SHOW, 'New call', 'New call', {
         positionClass: 'toast-bottom-center',
         disableTimeOut: true,
         progressBar: false,
         closeButton: false,
         tapToDismiss: false,
         toastComponent: RingingToastComponent,
      });

      const openToasts = _.cloneDeep(ctx.getState()?.openRingingPhoneToasts ?? []);
      const closingtoasts = _.cloneDeep(ctx.getState()?.closingRingingPhoneToasts ?? []);
      ctx.patchState({
         openRingingPhoneToasts: [toast.toastId],
         closingRingingPhoneToasts: _.difference(_.concat(openToasts, closingtoasts), [toast.toastId]),
      });
   }

   @Action(CloseOpenRingingPhoneNotifications)
   public closeOpenRingingPhoneNotifications(ctx: StateContext<NotificationToastrStateModel>, action: CloseOpenRingingPhoneNotifications) {
      const openToasts = _.cloneDeep(ctx.getState()?.openRingingPhoneToasts ?? []);
      const closingToasts = _.cloneDeep(ctx.getState()?.closingRingingPhoneToasts ?? []);
      ctx.patchState({
         openRingingPhoneToasts: [],
         closingRingingPhoneToasts: _.concat(openToasts, closingToasts),
      });
   }

   @Action(ToastNewWaitingItemNotification)
   public toastNewWaitingItemNotification(ctx: StateContext<NotificationToastrStateModel>, action: ToastNewWaitingItemNotification) {
      const linebreak = '<br/>';
      const header: string = this.translationService.instant('notification.waiting_item.title');
      let message = '';
      const skills = action.skills;

      if (skills.length === 0) {
         this.store.dispatch(new CloseOpenWaitingItemNotification());
      }

      const displayNames: Set<string> = new Set<string>();

      skills.forEach((skill) => {
         const waitCount = skills.filter((currentSkill) => currentSkill.skillId === skill.skillId).length;

         if (!displayNames.has(skill.displayName)) {
            displayNames.add(skill.displayName);

            message += `${this.translationService.instant('notification.waiting_item.new_waiting_items', {
               skillName: skill.displayName,
               waitCount: waitCount,
            })}${linebreak}`;
         }
      });

      const toast = this.notificationService.showDefaultToast(NotificationType.INFO, header, message, {
         disableTimeOut: true,
         progressBar: false,
         tapToDismiss: false,
         enableHtml: true,
         toastComponent: WaitingItemComponent,
      });

      const openToasts = _.cloneDeep(ctx.getState()?.openWaitingItemToasts ?? []);
      const closingtoasts = _.cloneDeep(ctx.getState()?.closingWaitingItemToasts ?? []);
      ctx.patchState({
         openWaitingItemToasts: [toast.toastId],
         closingWaitingItemToasts: _.difference(_.concat(openToasts, closingtoasts), [toast.toastId]),
      });
   }

   @Action(CloseOpenWaitingItemNotification)
   public closeOpenWaitingItemNotifications(ctx: StateContext<NotificationToastrStateModel>, action: CloseOpenWaitingItemNotification) {
      const openToasts = _.cloneDeep(ctx.getState()?.openWaitingItemToasts ?? []);
      const closingToasts = _.cloneDeep(ctx.getState()?.closingWaitingItemToasts ?? []);
      ctx.patchState({
         openWaitingItemToasts: [],
         closingWaitingItemToasts: _.concat(openToasts, closingToasts),
      });
   }

   @Action(ToastNewConfirmOutboundCallNotification)
   public toastNewConfirmOutboundCallNotification(ctx: StateContext<NotificationToastrStateModel>, action: ToastNewConfirmOutboundCallNotification) {
      const toast = this.notificationService.showDefaultToast(NotificationType.SHOW, 'Confirm Call', action.numberToDial, {
         positionClass: 'toast-bottom-center',
         disableTimeOut: true,
         progressBar: false,
         closeButton: false,
         tapToDismiss: false,
         toastComponent: OutboundConfirmationToastComponent,
      });

      const openToasts = _.cloneDeep(ctx.getState()?.openOutboundCallToasts ?? []);
      const closingtoasts = _.cloneDeep(ctx.getState()?.closingOutboundCallToasts ?? []);
      const outboundCallRequestContent = _.cloneDeep(ctx.getState()?.outboundCallRequestContent ?? []);
      ctx.patchState({
         openOutboundCallToasts: [toast.toastId],
         closingOutboundCallToasts: _.difference(_.concat(openToasts, closingtoasts), [toast.toastId]),
         outboundCallRequestContent: _.concat(outboundCallRequestContent, {
            notificationId: toast.toastId,
            numberToDial: action.numberToDial,
         }),
      });
   }

   @Action(CloseOpenConfirmOutboundCallNotifications)
   public closeOpenConfirmOutboundCallNotifications(ctx: StateContext<NotificationToastrStateModel>, action: CloseOpenConfirmOutboundCallNotifications) {
      const openToasts = _.cloneDeep(ctx.getState()?.openOutboundCallToasts ?? []);
      const closingToasts = _.cloneDeep(ctx.getState()?.closingOutboundCallToasts ?? []);
      ctx.patchState({
         openOutboundCallToasts: [],
         closingOutboundCallToasts: _.concat(openToasts, closingToasts),
      });
   }

   @Action(ToastNewChatNotification)
   public toastNewChatNotification(ctx: StateContext<NotificationToastrStateModel>, action: ToastNewChatNotification) {
      const toast = this.notificationService.showDefaultToast(NotificationType.SHOW, 'New chat', 'New chat', {
         positionClass: 'toast-bottom-center',
         disableTimeOut: true,
         progressBar: false,
         closeButton: false,
         tapToDismiss: false,
         toastComponent: ChatToastComponent,
      });
      const openToasts = _.cloneDeep(ctx.getState()?.openChatsToasts ?? []);
      const closingtoasts = _.cloneDeep(ctx.getState()?.closingChatsToasts ?? []);
      ctx.patchState({
         openChatsToasts: [toast.toastId],
         closingChatsToasts: _.difference(_.concat(openToasts, closingtoasts), [toast.toastId]),
      });
   }

   @Action(CloseOpenChatNotifications)
   public closeOpenChatNotifications(ctx: StateContext<NotificationToastrStateModel>, action: CloseOpenChatNotifications) {
      const openToasts = _.cloneDeep(ctx.getState()?.openChatsToasts ?? []);
      const closingToasts = _.cloneDeep(ctx.getState()?.closingChatsToasts ?? []);
      ctx.patchState({
         openChatsToasts: [],
         closingChatsToasts: _.concat(openToasts, closingToasts),
      });
   }

   @Action(ToastServerConnectionChanged)
   public toastServerConnectionChanged(ctx: StateContext<NotificationToastrStateModel>, action: ToastServerConnectionChanged) {
      if (action.connectionState === RealTimeConnectionStatus.CONNECTED && action.isConnectionConfirmedByServer) {
         _.forEach(_.cloneDeep(ctx.getState()?.openSignalRToasts ?? []), (openToastId) => {
            this.toastr.remove(openToastId);
         });
         ctx.patchState({
            openSignalRToasts: [],
         });
         return;
      }
      return this.notificationService
         .translateAndShowToast(NotificationType.WARN, 'notification.default.signalRDisconnected.title', 'notification.default.signalRDisconnected.message', {
            disableTimeOut: false,
            progressBar: false,
            closeButton: false,
            tapToDismiss: false,
         })
         .pipe(
            tap((toast) =>
               ctx.patchState({
                  openSignalRToasts: [..._.cloneDeep(ctx.getState()?.openSignalRToasts ?? []), toast.toastId],
               })
            )
         );
   }

   @Action(ToastNewApplicationVersionIsAvailable)
   public toastNewApplicationVersionIsAvailable(ctx: StateContext<NotificationToastrStateModel>, action: ToastNewApplicationVersionIsAvailable) {
      const toast$ = this.notificationService.translateAndShowToast(
         NotificationType.INFO,
         'notification.default.new_version_available.title',
         'notification.default.new_version_available.message',
         {
            disableTimeOut: true,
            progressBar: false,
         }
      );
      toast$
         .pipe(
            switchMap((x) => x.onTap),
            tap(() => console.info('Manually refresh window to load newest application version')),
            tap(() => window.location.reload())
         )
         .subscribe();
   }

   @Action(ToastUserInfoNotification)
   public toastUserInfoNotification(ctx: StateContext<NotificationToastrStateModel>, action: ToastUserInfoNotification) {
      return this.notificationService.translateAndShowToast(
         action.notificationType,
         action.titleTranslationKey,
         action.messageTranslationKey,
         action.toastConfig
      );
   }

   @Action(ToastUnreachableNotificationIfRequired)
   public toastUnreachableNotification(ctx: StateContext<NotificationToastrStateModel>, action: ToastUnreachableNotificationIfRequired) {
      // test reachability before creating toast, else an empty toast might appear.
      if (!this.store.selectSnapshot(SamwinUnreachableWarningSelectors.isUnreachable)) {
         return;
      }
      const toast = this.notificationService.showDefaultToast(NotificationType.WARN, 'unreachable-warning', 'unreachable-warning', {
         disableTimeOut: true,
         progressBar: false,
         closeButton: true,
         tapToDismiss: false,
         toastComponent: UnreachableToastComponent,
      });

      toast.onShown.toPromise().then(() => ctx.dispatch(new UnreachableNotificationAdded(toast.toastId)));
      toast.onHidden.toPromise().then(() => ctx.dispatch(new UnreachableNotificationRemoved(toast.toastId)));
   }

   @Action(UnreachableNotificationAdded)
   public unreachableNotificationAdded(ctx: StateContext<NotificationToastrStateModel>, action: UnreachableNotificationAdded) {
      const openToasts = _.cloneDeep(ctx.getState()?.openUnreachableToasts ?? []);
      const closingtoasts = _.cloneDeep(ctx.getState()?.closingUnreachableToasts ?? []);
      ctx.patchState({
         openUnreachableToasts: [action.toastId],
         closingUnreachableToasts: _.difference(_.concat(openToasts, closingtoasts), [action.toastId]),
      });
   }

   @Action(UnreachableNotificationRemoved)
   public UnreachableNotificationRemoved(ctx: StateContext<NotificationToastrStateModel>, action: UnreachableNotificationRemoved) {
      const openToasts = _.cloneDeep(ctx.getState()?.openUnreachableToasts ?? []);
      const closingtoasts = _.cloneDeep(ctx.getState()?.closingUnreachableToasts ?? []);
      ctx.patchState({
         openUnreachableToasts: _.difference(openToasts, [action.toastId]),
         closingUnreachableToasts: _.concat(closingtoasts, [action.toastId]),
      });
   }

   @Action(CloseUnreachableNotification)
   public closeUnreachableNotification(ctx: StateContext<NotificationToastrStateModel>, action: CloseUnreachableNotification) {
      const openToasts = _.cloneDeep(ctx.getState()?.openUnreachableToasts ?? []);
      const closingToasts = _.cloneDeep(ctx.getState()?.closingUnreachableToasts ?? []);
      ctx.patchState({
         openUnreachableToasts: [],
         closingUnreachableToasts: _.concat(openToasts, closingToasts),
      });
   }

   public SubscribeToUnreachableNotification() {
      combineLatest([
         this.store.select(SessionState.getUserStateNotificationwindowRepeat),
         this.store.select(SessionState.getUserStateNotificationwindowInterval),
      ])
         .pipe(
            switchMap(([canRepeat, interval]) =>
               iif(
                  () => canRepeat,
                  combineLatest([
                     this.store.select(SamwinUnreachableWarningSelectors.isUnreachable),
                     this.store.select(NotificationToastrState.isUnreachableToastOpen),
                  ]).pipe(
                     distinctUntilChanged(),
                     debounceTime(interval.asMilliseconds()),
                     filter(([isUnreachable, isUnreachableToastOpen]) => isUnreachable && !isUnreachableToastOpen && interval.asMilliseconds() > 0),
                     switchMap(() => this.store.dispatch(new ToastUnreachableNotificationIfRequired()))
                  ),
                  this.store.select(SamwinUnreachableWarningSelectors.isUnreachable).pipe(
                     distinctUntilChanged(),
                     debounceTime(interval.asMilliseconds()),
                     filter((isUnreachable) => isUnreachable && interval.asMilliseconds() > 0),
                     switchMap(() => {
                        if (this.store.selectSnapshot(NotificationToastrState.isUnreachableToastOpen)) {
                           return of(null);
                        }
                        return this.store.dispatch(new ToastUnreachableNotificationIfRequired());
                     })
                  )
               )
            )
         )
         .subscribe();

      this.store
         .select(SamwinUnreachableWarningSelectors.isUnreachable)
         .pipe(
            filter((isUnreachable) => !isUnreachable),
            switchMap(() => this.store.dispatch(new CloseUnreachableNotification()))
         )
         .subscribe();
   }

   @Action(RequestNewOutboundCall)
   public requestNewOutboundCall(ctx: StateContext<NotificationToastrStateModel>, action: RequestNewOutboundCall) {
      return ctx
         .dispatch(new DialpadNumberChangedAction(action.requestData.numberToDial, null))
         .pipe(switchMap(() => ctx.dispatch(new ToastNewConfirmOutboundCallNotification(action.requestData.numberToDial))));
   }

   @Action(CreateNativeOutboundCallWithToastId)
   public createNativeOutboundCallWithToastId(ctx: StateContext<NotificationToastrStateModel>, action: CreateNativeOutboundCallWithToastId) {
      const outboundCallRequestEntry = ctx.getState().outboundCallRequestContent.find((call) => call.notificationId === action.toastId);
      return this.nativeNotificationService.createNativeNotificationWithParameter$(action.tag, action.toastId, outboundCallRequestEntry?.numberToDial);
   }

   @Action(AcceptNewOutboundCallRequest)
   public acceptNewOutboundCallRequest(ctx: StateContext<NotificationToastrStateModel>, action: AcceptNewOutboundCallRequest) {
      const outboundCallRequestEntry = ctx.getState().outboundCallRequestContent.find((call) => call.notificationId === action.notificationId);
      if (_.isNil(outboundCallRequestEntry.numberToDial)) {
         return;
      }
      ctx.dispatch(new UpdateOutboundCallRequestContent(action.notificationId));
      ctx.dispatch(new StartNewCallAction(outboundCallRequestEntry.numberToDial));
   }

   @Action(UpdateOutboundCallRequestContent)
   public updateOutboundCallRequestContent(ctx: StateContext<NotificationToastrStateModel>, action: UpdateOutboundCallRequestContent) {
      const updatedOutboundCallRequestContent = ctx.getState().outboundCallRequestContent.filter((call) => call.notificationId !== action.notificationId);
      ctx.patchState({
         outboundCallRequestContent: updatedOutboundCallRequestContent,
      });
   }

   @Action(AgentIsBeingLoggedOutNotification)
   public agentIsBeingLoggedOutNotification(ctx: StateContext<NotificationToastrStateModel>, action: AgentIsBeingLoggedOutNotification) {
      const timeLeft = moment(action.timestamp).diff(moment(), 'seconds');
      const title = this.translationService.instant('notification.agent_being_logged_out.title');
      const message = this.translationService.instant('notification.agent_being_logged_out.message', { timeLeft: timeLeft.toString() });
      this.notificationService.showDefaultToast(NotificationType.WARN, title, message, {
         disableTimeOut: true,
         progressBar: false,
      });
      return this.nativeNotificationService.displayNotification(title, message);
   }

   @Action(RemoveSingularToast)
   public removeSingularToast(ctx: StateContext<NotificationToastrStateModel>, action: RemoveSingularToast) {
      this.toastr.remove(action.toastId);
   }

   @Action(CreateNativeWithToastId)
   public createNativeWithToastId(ctx: StateContext<NotificationToastrStateModel>, action: CreateNativeWithToastId) {
      return this.nativeNotificationService.createNativeNotification$(action.tag, action.toastId).pipe(take(1));
   }

   @Action(CloseNativeWithToastId)
   public closeNativeWithToastId(ctx: StateContext<NotificationToastrStateModel>, action: CloseNativeWithToastId) {
      return this.nativeNotificationService.closeNotificationWithTag$(action.tag, action.toastId);
   }

   @Action(MarkRequireMoreAgentsAsClosed)
   public MarkRequireMoreAgentsAsClosed(ctx: StateContext<NotificationToastrStateModel>, action: MarkRequireMoreAgentsAsClosed) {
      const closingRequireMoreAgentsToasts = _.cloneDeep(ctx.getState()?.closingRequireMoreAgentsToasts ?? []);
      ctx.patchState({
         closingRequireMoreAgentsToasts: _.difference(closingRequireMoreAgentsToasts, [action.toastId]),
      });
   }

   @Action(MarkUnreachableAsClosed)
   public MarkUnreachableAsClosed(ctx: StateContext<NotificationToastrStateModel>, action: MarkUnreachableAsClosed) {
      const closingUnreachableToasts = _.cloneDeep(ctx.getState()?.closingUnreachableToasts ?? []);
      ctx.patchState({
         closingUnreachableToasts: _.difference(closingUnreachableToasts, [action.toastId]),
      });
   }

   @Action(MarkAwaitingCallAsClosed)
   public MarkAwaitingCallAsClosed(ctx: StateContext<NotificationToastrStateModel>, action: MarkAwaitingCallAsClosed) {
      const closingRingingPhoneToasts = _.cloneDeep(ctx.getState()?.closingRingingPhoneToasts ?? []);
      ctx.patchState({
         closingRingingPhoneToasts: _.difference(closingRingingPhoneToasts, [action.toastId]),
      });
   }

   @Action(MarkOutboundCallAsClosed)
   public MarkOutboundCallAsClosed(ctx: StateContext<NotificationToastrStateModel>, action: MarkOutboundCallAsClosed) {
      const closingOutboundCallToasts = _.cloneDeep(ctx.getState()?.closingOutboundCallToasts ?? []);
      ctx.patchState({
         closingOutboundCallToasts: _.difference(closingOutboundCallToasts, [action.toastId]),
      });
   }

   @Action(MarkWaitingItemAsClosed)
   public MarkWaitingItemAsClosed(ctx: StateContext<NotificationToastrStateModel>, action: MarkWaitingItemAsClosed) {
      const closingWaitingItemToasts = _.cloneDeep(ctx.getState()?.closingWaitingItemToasts ?? []);
      ctx.patchState({
         closingWaitingItemToasts: _.difference(closingWaitingItemToasts, [action.toastId]),
      });
   }

   @Action(MarkAwaitingChatAsClosed)
   public MarkAwaitingChatAsClosed(ctx: StateContext<NotificationToastrStateModel>, action: MarkAwaitingChatAsClosed) {
      const closingChatsToasts = _.cloneDeep(ctx.getState()?.closingChatsToasts ?? []);
      ctx.patchState({
         closingChatsToasts: _.difference(closingChatsToasts, [action.toastId]),
      });
   }

   @Action(MarkAwaitingMessageAsClosed)
   public MarkAwaitingMessageAsClosed(ctx: StateContext<NotificationToastrStateModel>, action: MarkAwaitingMessageAsClosed) {
      const closingAssignedMessagesToasts = _.cloneDeep(ctx.getState()?.closingAssignedMessagesToasts ?? []);
      ctx.patchState({
         closingAssignedMessagesToasts: _.difference(closingAssignedMessagesToasts, [action.toastId]),
      });
   }

   @Action(MarkAsClosedByTag)
   public markAsClosedByTag(ctx: StateContext<NotificationToastrStateModel>, action: MarkAsClosedByTag) {
      if (action.tag === ServiceWorkerStaticValues.AWAITING_CALL_NOTIFICATION_TAG) {
         ctx.dispatch(new MarkAwaitingCallAsClosed(action.toastId));
         return;
      }
      if (action.tag === ServiceWorkerStaticValues.WAITING_ITEM_NOTIFICATION_TAG) {
         ctx.dispatch(new MarkWaitingItemAsClosed(action.toastId));
         return;
      }
      if (action.tag === ServiceWorkerStaticValues.NEW_OUTBOUND_CALL_REQUEST_TAG) {
         ctx.dispatch(new MarkOutboundCallAsClosed(action.toastId));
         return;
      }
      if (action.tag === ServiceWorkerStaticValues.ASSIGNED_MESSAGE_NOTIFICATION_TAG) {
         ctx.dispatch(new MarkAwaitingMessageAsClosed(action.toastId));
         return;
      }
      if (action.tag === ServiceWorkerStaticValues.AWAITING_CHAT_NOTIFICATION_TAG) {
         ctx.dispatch(new MarkAwaitingChatAsClosed(action.toastId));
         return;
      }
      if (action.tag === ServiceWorkerStaticValues.REQUIRE_AGENTS_NOTIFICATION_TAG) {
         ctx.dispatch(new MarkRequireMoreAgentsAsClosed(action.toastId));
         return;
      }
      if (action.tag === ServiceWorkerStaticValues.UNREACHABLE_NOTIFICATION_TAG) {
         ctx.dispatch(new MarkUnreachableAsClosed(action.toastId));
         return;
      }
   }
}
